import { HStack, Td, Tr } from "@chakra-ui/react";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import EntityProfileTable from "../../../../entityProfileModal/components/EntityProfileTable";
import type { CaregiverProfileBridgeActions } from "../../../caregiverProfileModal.actions";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import {
  formatCaregiverProfileValue,
  getCaregiverFullName,
} from "../../../caregiverProfileModal.utils";
import { useCaregiverVisitEngagements } from "./CaregiverProfileVisitsBroadcastedSection.hooks";

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiver?: CaregiverProfileCaregiver;
  caregiverId: CaregiverId;
};

function formatDateTime(value: unknown) {
  return formatCaregiverProfileValue(value) === ""
    ? ""
    : value instanceof Object
    ? dateFormatter.toDateTime(value as never)
    : String(value);
}

export default function CaregiverProfileVisitsBroadcastedSection(props: Props) {
  const engagementsQuery = useCaregiverVisitEngagements(props.caregiverId);
  const engagements = engagementsQuery.data?.engagements ?? [];

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load broadcasted visits."
      isError={engagementsQuery.isError}
      isLoading={engagementsQuery.isLoading}
      loadingLabel="Loading broadcasted visits"
      onRetry={() => void engagementsQuery.refetch()}
    >
      {engagements.length === 0 ? null : (
        <EntityProfileTable
          columns={[
            "Patient Name",
            "Broadcast",
            "Assigned To",
            "Start Time",
            "Type",
            "Viewed",
            "Requested",
            "Cancelled Request",
            "Not Interested",
            "Not Interested Reason",
            "Actions",
          ]}
        >
          {engagements.map((engagement) => (
            <Tr
              key={`${engagement.visit.id}-${formatDateTime(engagement.broadcastAt)}`}
              _hover={{ bg: "#fafafa" }}
              cursor="pointer"
              onClick={() => props.actions.navigateToVisitBroadcast(engagement.visit.id)}
            >
              <Td>
                <EntityModalButton
                  kind="blue"
                  variant="link"
                  onClick={(event) => {
                    event.stopPropagation();
                    props.actions.navigateToPatient(engagement.visit.patientId);
                  }}
                >
                  {engagement.visit.patientName}
                </EntityModalButton>
              </Td>
              <Td>{formatDateTime(engagement.broadcastAt ?? engagement.visit.createdAt)}</Td>
              <Td>
                {engagement.visit.assignedCaregiver === null ? (
                  ""
                ) : (
                  <EntityModalButton
                    kind="blue"
                    variant="link"
                    onClick={(event) => {
                      event.stopPropagation();

                      if (engagement.visit.assignedCaregiver !== null) {
                        props.actions.navigateToCaregiver(engagement.visit.assignedCaregiver);
                      }
                    }}
                  >
                    {engagement.visit.assignedCaregiver === props.caregiverId &&
                    props.caregiver !== undefined
                      ? getCaregiverFullName(props.caregiver)
                      : `Caregiver #${formatCaregiverProfileValue(
                          engagement.visit.assignedCaregiver
                        )}`}
                  </EntityModalButton>
                )}
              </Td>
              <Td>{formatDateTime(engagement.visit.startTime)}</Td>
              <Td>{engagement.visit.isSingleVisit ? "Single" : "Recurring"}</Td>
              <Td>{formatDateTime(engagement.viewedAt)}</Td>
              <Td>{formatDateTime(engagement.requestedAt)}</Td>
              <Td>{formatDateTime(engagement.cancelledAt)}</Td>
              <Td>{formatDateTime(engagement.notInterestedAt)}</Td>
              <Td>{formatCaregiverProfileValue(engagement.notInterestedReason)}</Td>
              <Td>
                <HStack spacing={3}>
                  <EntityModalButton
                    kind="blue"
                    variant="link"
                    onClick={(event) => {
                      event.stopPropagation();
                      props.actions.navigateToVisitBroadcast(engagement.visit.id);
                    }}
                  >
                    Open
                  </EntityModalButton>
                </HStack>
              </Td>
            </Tr>
          ))}
        </EntityProfileTable>
      )}
    </EntityProfileQuerySection>
  );
}
